import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE } from "../../utils/api";
import { formatPrice } from "../../utils/format";
import type { ListingResponse } from "./types";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useFavorites } from "../../hooks/useFavorites";
import { usePageTitle } from "../../hooks/usePageTitle";
import { CompareModal } from "./CompareModal";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { KakaoMapView } from "../../components/KakaoMapView";

// 매물 유형별 색상 (카드 컬러바용)
const TYPE_COLORS: Record<string, string> = {
  "농지": "#16A34A",
  "임야": "#92400E",
  "묘터": "#7C3AED",
  "공장": "#6B7280",
  "창고": "#EA580C",
  "상가": "#3B82F6",
  "주택": "#2563EB",
};
const DEFAULT_TYPE_COLOR = "#6B7280";

const PAGE_SIZE = 20;

export function ListingsPage() {
  usePageTitle("\uB9E4\uBB3C \uD0D0\uC0C9");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brokerIdFromUrl = searchParams.get("broker_id") || "";
  const m = useIsMobile();
  const { isFav, toggle: toggleFav } = useFavorites();
  const [ndaSigned, setNdaSigned] = useState<boolean | null>(null);

  // 매물 비교
  const [compareIds, setCompareIds] = useState<Set<string>>(new Set());
  const [showCompare, setShowCompare] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  function toggleCompare(id: string) {
    setCompareIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 3) {
        next.add(id);
      }
      return next;
    });
  }

  const [allListings, setAllListings] = useState<ListingResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [page, setPage] = useState(1);

  const [filterOptions, setFilterOptions] = useState<{
    sido: string[];
    sigungu: Record<string, string[]>;
    property_types: string[];
    land_categories: string[];
  }>({ sido: [], sigungu: {}, property_types: [], land_categories: [] });

  const [sido, setSido] = useState("");
  const [sigungu, setSigungu] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [landCategory, setLandCategory] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [areaMin, setAreaMin] = useState("");
  const [areaMax, setAreaMax] = useState("");
  const [zoning, setZoning] = useState("");
  const [roadAccess, setRoadAccess] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  // debounce용 state (가격/면적)
  const [debouncedPrice, setDebouncedPrice] = useState({ min: "", max: "" });
  const [debouncedArea, setDebouncedArea] = useState({ min: "", max: "" });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // NDA 서명 여부 확인 (비로그인 시 false, apiFetch 대신 fetch 직접 사용 - 401 리디렉션 방지)
  useEffect(() => {
    const token = localStorage.getItem("broker_token");
    if (!token) { setNdaSigned(false); return; }
    fetch(`${API_BASE}/api/nda/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setNdaSigned(d ? !!d.agreed : false))
      .catch(() => setNdaSigned(false));
  }, []);

  // 전체 매물 + 필터 옵션 로드
  useEffect(() => {
    fetch(`${API_BASE}/api/listings?limit=500`)
      .then((r) => r.json())
      .then((d) => setAllListings(d.listings || []))
      .catch(() => setError("매물을 불러오지 못했습니다"))
      .finally(() => setLoading(false));

    fetch(`${API_BASE}/api/listings/filters`)
      .then((r) => r.json())
      .then(setFilterOptions)
      .catch((e) => console.warn('filter options fetch failed:', e));
  }, []);

  // 가격/면적 debounce (300ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedPrice({ min: priceMin, max: priceMax });
      setDebouncedArea({ min: areaMin, max: areaMax });
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [priceMin, priceMax, areaMin, areaMax]);

  // 용도지역·접도현황 옵션 (실제 데이터 기반)
  const zoningOptions = useMemo(() =>
    Array.from(new Set(allListings.map((l) => l.zoning).filter(Boolean))).sort(),
    [allListings]
  );
  const roadAccessOptions = useMemo(() =>
    Array.from(new Set(allListings.map((l) => l.road_access).filter(Boolean))).sort(),
    [allListings]
  );

  // 클라이언트 필터 (가격/면적은 debounce 적용)
  const filtered = allListings.filter((l) => {
    if (brokerIdFromUrl && l.broker_id !== brokerIdFromUrl) return false;
    if (sido && l.region_sido !== sido) return false;
    if (sigungu && l.region_sigungu !== sigungu) return false;
    if (propertyType && l.property_type !== propertyType) return false;
    if (landCategory && l.land_category !== landCategory) return false;
    if (zoning && l.zoning !== zoning) return false;
    if (roadAccess && l.road_access !== roadAccess) return false;
    if (debouncedPrice.min && l.price_manwon < Number(debouncedPrice.min)) return false;
    if (debouncedPrice.max && l.price_manwon > Number(debouncedPrice.max)) return false;
    if (debouncedArea.min && l.area_pyeong < Number(debouncedArea.min)) return false;
    if (debouncedArea.max && l.area_pyeong > Number(debouncedArea.max)) return false;
    return true;
  });

  // 정렬
  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case "price_asc":   return a.price_manwon - b.price_manwon;
      case "price_desc":  return b.price_manwon - a.price_manwon;
      case "area_asc":    return a.area_pyeong - b.area_pyeong;
      case "area_desc":   return b.area_pyeong - a.area_pyeong;
      case "most_viewed": return b.view_count - a.view_count;
      default:            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paged = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // 필터 바뀌면 1페이지로
  useEffect(() => { setPage(1); }, [sido, sigungu, propertyType, landCategory, zoning, roadAccess, sortBy, debouncedPrice, debouncedArea]);

  // 시군구 목록 (API에서 받은 실제 데이터 기반)
  const sigunguList = sido ? (filterOptions.sigungu[sido] || []) : [];

  // 지도 마커 업데이트 키 (필터 변경 시 마커 갱신 트리거)
  const filteredKey = `${sido}|${sigungu}|${propertyType}|${landCategory}|${zoning}|${roadAccess}|${debouncedPrice.min}|${debouncedPrice.max}|${debouncedArea.min}|${debouncedArea.max}|${allListings.length}`;

  function resetFilters() {
    setSido(""); setSigungu(""); setPropertyType(""); setLandCategory("");
    setZoning(""); setRoadAccess(""); setSortBy("newest");
    setPriceMin(""); setPriceMax(""); setAreaMin(""); setAreaMax("");
  }

  // 페이지 번호 목록 생성
  function pageNums(): (number | "…")[] {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "…", totalPages];
    if (page >= totalPages - 3) return [1, "…", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "…", page - 1, page, page + 1, "…", totalPages];
  }

  const st = styles(m);

  return (
    <div style={st.page}>
      <AppHeader />

      <div style={st.content}>
        {/* NDA 안내 배너 (미서명 상태에서만 표시) */}
        {ndaSigned === false && (
          <div style={{
            background: "linear-gradient(135deg, rgba(212,168,83,0.07) 0%, rgba(212,168,83,0.03) 100%)",
            border: "1px solid rgba(212,168,83,0.22)",
            borderLeft: "3px solid #D4A853",
            borderRadius: "var(--radius-xl)",
            padding: m ? "14px 16px" : "16px 24px",
            marginBottom: m ? 12 : 16,
            display: "flex",
            alignItems: m ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap" as const,
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: 1 }}>
              <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{"\uD83D\uDCCB"}</span>
              <div>
                <p style={{ margin: "0 0 2px", fontSize: m ? 13 : 14, fontWeight: 600, color: "#D4A853" }}>
                  NDA 서명 후 정확한 위치·가격·소유자 정보가 열립니다
                </p>
                <p style={{ margin: 0, fontSize: m ? 12 : 13, color: "#94A3B8" }}>
                  지금 서명하면 350건 매물의 실제 정보를 즉시 열람할 수 있습니다
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/nda")}
              style={{
                padding: m ? "8px 16px" : "9px 20px",
                borderRadius: 8, border: "none",
                background: "linear-gradient(135deg, #D4A853, #C49A3C)",
                color: "#080E1A", fontSize: m ? 12 : 13, fontWeight: 700,
                cursor: "pointer", whiteSpace: "nowrap" as const, flexShrink: 0,
              }}
            >
              NDA 서명하기
            </button>
          </div>
        )}

        {/* 중개사 필터 안내 배너 */}
        {brokerIdFromUrl && (
          <div style={{
            background: "rgba(22,163,74,0.08)",
            border: "1px solid rgba(22,163,74,0.25)",
            borderLeft: "3px solid #16A34A",
            borderRadius: "var(--radius-xl)",
            padding: m ? "12px 16px" : "14px 20px",
            marginBottom: m ? 12 : 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}>
            <span style={{ fontSize: m ? 13 : 14, color: "#86EFAC" }}>
              🏡 담당 중개사 매물만 표시 중 ({filtered.length}건)
            </span>
            <button
              onClick={() => navigate("/listings")}
              style={{
                padding: "6px 14px", borderRadius: 6, border: "1px solid rgba(22,163,74,0.4)",
                background: "transparent", color: "#86EFAC", fontSize: 12, cursor: "pointer",
              }}
            >
              전체 보기
            </button>
          </div>
        )}

        {/* 필터 바 */}
        <div style={st.filterBar}>
          <div style={st.filterRow}>
            <select value={sido} onChange={(e) => { setSido(e.target.value); setSigungu(""); }} style={st.select}>
              <option value="">전체 시도</option>
              {filterOptions.sido.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={sigungu} onChange={(e) => setSigungu(e.target.value)} style={{ ...st.select, opacity: sido ? 1 : 0.5 }} disabled={!sido}>
              <option value="">전체 시군구</option>
              {sigunguList.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} style={st.select}>
              <option value="">전체 유형</option>
              {filterOptions.property_types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={landCategory} onChange={(e) => setLandCategory(e.target.value)} style={st.select}>
              <option value="">전체 지목</option>
              {filterOptions.land_categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={zoning} onChange={(e) => setZoning(e.target.value)} style={st.select}>
              <option value="">전체 용도지역</option>
              {zoningOptions.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
            <select value={roadAccess} onChange={(e) => setRoadAccess(e.target.value)} style={st.select}>
              <option value="">전체 접도현황</option>
              {roadAccessOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input placeholder="최소 가격(만원)" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} style={st.input} type="number" />
            <input placeholder="최대 가격(만원)" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} style={st.input} type="number" />
            <input placeholder="최소 면적(평)" value={areaMin} onChange={(e) => setAreaMin(e.target.value)} style={st.input} type="number" />
            <input placeholder="최대 면적(평)" value={areaMax} onChange={(e) => setAreaMax(e.target.value)} style={st.input} type="number" />
            <button onClick={resetFilters} style={st.resetBtn}>초기화</button>
          </div>
          <div style={st.filterMeta}>
            <span style={st.countLabel}>총 <strong>{filtered.length}</strong>건</span>
            <div style={{ display: "flex", alignItems: "center", gap: m ? 6 : 10 }}>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={st.sortSelect}>
              <option value="newest">최신순</option>
              <option value="price_asc">가격 낮은순</option>
              <option value="price_desc">가격 높은순</option>
              <option value="area_asc">면적 작은순</option>
              <option value="area_desc">면적 큰순</option>
              <option value="most_viewed">조회수 많은순</option>
            </select>
            <div style={st.viewToggle}>
              <button onClick={() => setViewMode("list")} style={viewMode === "list" ? st.toggleActive : st.toggleBtn}>목록</button>
              <button onClick={() => setViewMode("map")} style={viewMode === "map" ? st.toggleActive : st.toggleBtn}>지도</button>
            </div>
            </div>
          </div>
        </div>

        {error && <p style={st.error}>{error}</p>}

        {/* 목록 뷰 */}
        {viewMode === "list" && (
          <>
            {loading ? (
              <div style={st.centerMsg}>매물을 불러오는 중...</div>
            ) : paged.length === 0 ? (
              <div style={st.centerMsg}>조건에 맞는 매물이 없습니다</div>
            ) : (
              <div style={st.grid}>
                {paged.map((l) => {
                  const isSelected = compareIds.has(l.id);
                  const typeColor = TYPE_COLORS[l.property_type] || DEFAULT_TYPE_COLOR;
                  const perPyeong = l.area_pyeong > 0 ? Math.round(l.price_manwon / l.area_pyeong) : 0;
                  return (
                  <div
                    key={l.id}
                    style={{
                      ...st.card,
                      borderLeft: `4px solid ${typeColor}`,
                      ...(isSelected ? st.cardSelected : {}),
                      ...(hoveredCard === l.id ? st.cardHover : {}),
                    }}
                    onClick={() => navigate(`/listings/${l.id}`)}
                    onMouseEnter={() => setHoveredCard(l.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    {/* 헤더: 비교 체크 + 유형 배지 + 시도 + 하트 */}
                    <div style={st.cardHeader}>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleCompare(l.id); }}
                        style={isSelected ? st.checkActive : st.checkBtn}
                        title={isSelected ? "비교 해제" : (compareIds.size >= 3 ? "최대 3개까지 선택 가능" : "비교에 추가")}
                      >
                        {isSelected ? "✓" : "+"}
                      </button>
                      <span style={st.badge}>{l.property_type}</span>
                      <span style={st.cardSido}>{l.region_sido}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFav(l.id); }}
                        style={isFav(l.id) ? st.heartActive : st.heartBtn}
                        title={isFav(l.id) ? "\uCC1C \uD574\uC81C" : "\uCC1C\uD558\uAE30"}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={isFav(l.id) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                      </button>
                    </div>
                    <div style={st.cardLocation}>{l.region_sigungu} {l.region_eupmyeondong}</div>
                    <div style={st.cardPrice}>{formatPrice(l.price_manwon)}<span style={st.priceUnit}>원</span></div>
                    <div style={st.cardArea}>
                      {l.area_pyeong.toLocaleString()}평
                      {perPyeong > 0 && <span style={st.perPyeong}>· 평당 {formatPrice(perPyeong)}원</span>}
                    </div>
                    {l.description && (
                      <div style={st.cardDesc}>{l.description.length > 55 ? l.description.slice(0, 55) + "…" : l.description}</div>
                    )}
                    <div style={st.cardFooter}>
                      {l.zoning && <span style={st.tag}>{l.zoning}</span>}
                      {l.broker_is_verified === true && <span style={st.verifiedTag}>{"\u2713 \uC778\uC99D"}</span>}
                      {l.broker_is_verified === false && <span style={st.pendingTag}>{"\uD83D\uDD50 \uC778\uC99D \uB300\uAE30"}</span>}
                      <span style={st.viewCount}>조회 {l.view_count}</span>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div style={st.pagination}>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={page === 1 ? st.pageDisabled : st.pageBtn}>{"‹"}</button>
                {pageNums().map((n, i) =>
                  n === "…" ? (
                    <span key={`e${i}`} style={st.pageEllipsis}>…</span>
                  ) : (
                    <button key={n} onClick={() => setPage(n as number)} style={n === page ? st.pageActive : st.pageBtn}>{n}</button>
                  )
                )}
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={page === totalPages ? st.pageDisabled : st.pageBtn}>{"›"}</button>
              </div>
            )}
          </>
        )}

        {/* 매물 비교 플로팅 버튼 */}
        {compareIds.size >= 2 && (
          <div style={st.floatWrap}>
            <button
              style={st.floatClearBtn}
              onClick={() => setCompareIds(new Set())}
              title="선택 초기화"
            >
              {"✕"}
            </button>
            <button style={st.floatBtn} onClick={() => setShowCompare(true)}>
              {compareIds.size}개 매물 비교하기
            </button>
          </div>
        )}

        {/* 비교 모달 */}
        {showCompare && (
          <CompareModal
            listings={allListings.filter((l) => compareIds.has(l.id))}
            onClose={() => setShowCompare(false)}
          />
        )}

        {/* 지도 뷰 */}
        <div>
          <KakaoMapView
            active={viewMode === "map"}
            updateKey={filteredKey}
            height={m ? "calc(100dvh - 160px)" : "calc(100vh - 210px)"}
            listings={filtered}
            allListings={allListings}
            interactive={true}
            blindNotice={"\uD83D\uDCCD \uB9E4\uBB3C \uC704\uCE58\uB294 \uBCF4\uC548\uC744 \uC704\uD574 \uB300\uB7B5\uC801\uC73C\uB85C \uD45C\uC2DC\uB429\uB2C8\uB2E4"}
          />
        </div>


      </div>
      <AppFooter />
    </div>
  );
}

function styles(m: boolean): Record<string, React.CSSProperties> {
  return {
    page: { minHeight: "100vh", background: "var(--bg-primary)" },
    header: { background: "var(--bg-card)", borderBottom: "1px solid var(--border-light)", position: "sticky", top: 0, zIndex: 100 },
    headerInner: { maxWidth: 1200, margin: "0 auto", padding: m ? "0 16px" : "0 32px", height: m ? 48 : 56, display: "flex", alignItems: "center", justifyContent: "space-between" },
    logo: { fontSize: m ? 16 : 18, fontWeight: 800, color: "var(--lt-gold)", cursor: "pointer", letterSpacing: "-0.5px" },
    nav: { display: "flex", alignItems: "center", gap: m ? 8 : 16 },
    navLink: { fontSize: m ? 13 : 14, color: "var(--text-secondary)", textDecoration: "none", fontWeight: 500 },
    navBtn: { fontSize: m ? 12 : 13, color: "#0F172A", background: "var(--lt-gold)", padding: m ? "5px 10px" : "6px 14px", borderRadius: 20, textDecoration: "none", fontWeight: 600 },
    content: { maxWidth: 1200, margin: "0 auto", padding: m ? "12px 16px 32px" : "20px 32px 40px" },
    filterBar: { background: "var(--bg-card)", border: "1px solid var(--border-light)", borderRadius: "var(--radius-xl)", padding: m ? "12px" : "16px 20px", marginBottom: m ? 12 : 20 },
    filterRow: { display: "flex", gap: m ? 6 : 8, flexWrap: "wrap" as const, marginBottom: m ? 8 : 12 },
    select: { padding: "7px 10px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: m ? 16 : 14, ...(m ? { flex: "1 1 calc(50% - 3px)", minWidth: 0 } : { minWidth: 110 }) },
    input: { padding: "7px 10px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: m ? 16 : 14, ...(m ? { flex: "1 1 calc(50% - 3px)", minWidth: 0 } : { width: 110 }) },
    resetBtn: { padding: "7px 14px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "transparent", color: "var(--text-muted)", fontSize: 14, cursor: "pointer", ...(m && { flex: "1 1 100%" }) },
    filterMeta: { display: "flex", alignItems: "center", justifyContent: "space-between" },
    countLabel: { fontSize: m ? 13 : 15, color: "#CBD5E1" },
    viewToggle: { display: "flex", gap: 4 },
    toggleBtn: { padding: m ? "5px 12px" : "6px 16px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "transparent", color: "var(--text-secondary)", fontSize: 14, cursor: "pointer" },
    toggleActive: { padding: m ? "5px 12px" : "6px 16px", borderRadius: "var(--radius-md)", border: "1px solid var(--lt-gold)", background: "var(--lt-gold)", color: "#0F172A", fontSize: 14, fontWeight: 700, cursor: "pointer" },
    sortSelect: { padding: "6px 10px", borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontSize: m ? 16 : 14, cursor: "pointer" },
    error: { color: "var(--lt-red)", background: "rgba(239,68,68,0.1)", padding: "10px 16px", borderRadius: "var(--radius-md)", fontSize: 14, marginBottom: 16 },
    centerMsg: { textAlign: "center" as const, padding: m ? "40px 0" : "60px 0", color: "var(--text-muted)", fontSize: 15 },
    grid: { display: "grid", gridTemplateColumns: m ? "1fr" : "repeat(auto-fill, minmax(270px, 1fr))", gap: m ? 14 : 22 },
    card: { background: "var(--bg-card)", border: "1px solid var(--border-light)", borderRadius: "var(--radius-xl)", padding: m ? "14px 16px" : "18px 20px", cursor: "pointer", transition: "border-color 0.15s, filter 0.15s", userSelect: "none" as const },
    cardHover: { filter: "brightness(1.07)" },
    cardHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: m ? 10 : 12 },
    badge: { display: "inline-block", padding: "3px 10px", borderRadius: 20, background: "rgba(184,151,46,0.15)", color: "var(--lt-gold)", fontSize: 13, fontWeight: 700, flexShrink: 0 },
    cardSido: { fontSize: 13, color: "#CBD5E1", flexShrink: 0 },
    cardLocation: { fontSize: m ? 14 : 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 },
    cardPrice: { fontSize: m ? 20 : 22, fontWeight: 800, color: "#FBBF24", marginBottom: 3 },
    priceUnit: { fontSize: 14, fontWeight: 400, color: "var(--text-muted)", marginLeft: 3 },
    cardArea: { fontSize: 14, color: "#CBD5E1", marginBottom: 8, display: "flex", alignItems: "center" },
    perPyeong: { fontSize: 13, color: "var(--text-muted)", marginLeft: 8 },
    cardDesc: { fontSize: 14, color: "#CBD5E1", lineHeight: 1.5, marginBottom: 10, borderTop: "1px solid var(--border-light)", paddingTop: 8 },
    cardFooter: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 },
    tag: { fontSize: 12, padding: "2px 8px", borderRadius: 20, background: "var(--bg-secondary)", color: "var(--text-muted)", border: "1px solid var(--border-light)" },
    verifiedTag: { fontSize: 12, padding: "2px 8px", borderRadius: 20, background: "rgba(16,185,129,0.12)", color: "var(--lt-green)", border: "1px solid rgba(16,185,129,0.2)" },
    pendingTag: { fontSize: 12, padding: "2px 8px", borderRadius: 20, background: "rgba(100,116,139,0.12)", color: "#94A3B8", border: "1px solid rgba(100,116,139,0.18)" },
    viewCount: { fontSize: 13, color: "var(--text-muted)" },
    pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: 4, marginTop: m ? 24 : 32 },
    pageBtn: { width: m ? 32 : 36, height: m ? 32 : 36, borderRadius: "var(--radius-md)", border: "1px solid var(--border-medium)", background: "var(--bg-card)", color: "var(--text-secondary)", fontSize: m ? 13 : 14, cursor: "pointer" },
    pageActive: { width: m ? 32 : 36, height: m ? 32 : 36, borderRadius: "var(--radius-md)", border: "1px solid var(--lt-gold)", background: "var(--lt-gold)", color: "#0F172A", fontSize: m ? 13 : 14, fontWeight: 700, cursor: "pointer" },
    pageDisabled: { width: m ? 32 : 36, height: m ? 32 : 36, borderRadius: "var(--radius-md)", border: "1px solid var(--border-light)", background: "transparent", color: "var(--border-medium)", fontSize: m ? 13 : 14, cursor: "default" },
    pageEllipsis: { width: m ? 32 : 36, textAlign: "center" as const, color: "var(--text-muted)", fontSize: m ? 13 : 14, lineHeight: m ? "32px" : "36px" },
    heartBtn: { width: 30, height: 30, borderRadius: "50%", background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, marginLeft: "auto", flexShrink: 0, transition: "color 0.15s" },
    heartActive: { width: 30, height: 30, borderRadius: "50%", background: "rgba(239,68,68,0.1)", border: "none", color: "#EF4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, marginLeft: "auto", flexShrink: 0 },
    // 비교 체크박스
    checkBtn: { width: 26, height: 26, borderRadius: 6, border: "1.5px solid var(--border-medium)", background: "var(--bg-secondary)", color: "var(--text-muted)", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0, transition: "border-color 0.15s, background 0.15s" },
    checkActive: { width: 26, height: 26, borderRadius: 6, border: "1.5px solid var(--lt-gold)", background: "rgba(212,168,83,0.15)", color: "var(--lt-gold)", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 },
    cardSelected: { borderColor: "var(--lt-gold)", boxShadow: "0 0 0 2px rgba(212,168,83,0.25)" },
    // 비교 플로팅 버튼
    floatWrap: { position: "fixed" as const, bottom: m ? 20 : 28, left: "50%", transform: "translateX(-50%)", zIndex: 300, display: "flex", alignItems: "center", gap: 8 },
    floatBtn: { padding: m ? "13px 28px" : "14px 36px", borderRadius: 50, border: "none", background: "linear-gradient(135deg,#D4A843,#B8922E)", color: "#0F172A", fontSize: m ? 14 : 15, fontWeight: 700, cursor: "pointer", boxShadow: "0 6px 24px rgba(212,168,83,0.45)", whiteSpace: "nowrap" as const },
    floatClearBtn: { width: 40, height: 40, borderRadius: "50%", border: "none", background: "rgba(15,23,42,0.9)", color: "rgba(255,255,255,0.7)", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, boxShadow: "0 4px 16px rgba(0,0,0,0.3)", flexShrink: 0 },
  };
}
